import React, { useMemo } from 'react';
import { Box, Typography, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { observer } from 'mobx-react-lite';
import CommonInput from '../../component/form/common-input.component';
import LinechartOfBusinessData, {
    mergeBusinessLineApexOptions,
    createDashboardBusinessLineApexOptions,
} from '../../component/charts/line-chart-of-business-data';
import PieChartOfBusinessData from '../../component/charts/pie-chart-of-business-data';
import i18n from '../../i18n';
import { formatEUR } from '../../constants/tool.fn';
import groupRevenueAnalysisState from './group-revenue-analysis-state';
import { accountState } from '../my-account/account-state';
import { getLineChartSeriesColors, getPieChartColorsByValues } from '../../themes/tokens/data-chart-tokens';
import { ReactComponent as StoreRankingIcon } from '../../static/images/svg/bxs_store.svg';
import type { Option } from '../../component/form/select-input-multiple';
import {
    GROUP_REVENUE_MULTI_STORE_LINE_CHART_CARD_MIN_HEIGHT_PX,
    GROUP_REVENUE_MULTI_STORE_TAB_FILL_VIEWPORT_MIN_HEIGHT_PX,
} from './group-revenue-analysis-layout';

const chartLabelColor = 'var(--text-neutral-500)';
const chartGridColor = 'var(--stroke-neutral-800-a25)';
/** 数据类型选项：总额 / 堂食 / 外卖 */
const DATA_TYPE_OPTIONS = [
    { value: 'total', label: 'group_revenue_analysis_data_type_total' },
    { value: 'dine_in', label: 'group_revenue_analysis_data_type_dine_in' },
    { value: 'delivery', label: 'group_revenue_analysis_data_type_delivery' }
];

/**
 * 多门店数据对比 Tab：门店卡片（营收、支付占比）、选中门店的营收趋势折线图
 */
const MultiStoreComparison: React.FC = observer(() => {
    const theme = useTheme();
    /** 大屏：Tab 拉满内容区，折线外卡 min 480px 并 flex 伸展 */
    const fillsViewportOnLargeScreen = useMediaQuery(
        `(min-height: ${GROUP_REVENUE_MULTI_STORE_TAB_FILL_VIEWPORT_MIN_HEIGHT_PX}px)`,
    );
    const state = groupRevenueAnalysisState;
    const dashboard = state.dashboard;
    const trend = state.trend;
    const loading = state.loadingMultiStoreComparison;
    const groups = accountState.userInfo?.responsible_group ?? [];
    const effectiveGroupId = state.selectedGroupStringId || (groups[0]?.string_id ?? '');

    const breakdown = dashboard?.breakdown ?? [];
    const selectedStoreIds = state.selectedStoreIds ?? [];
    // const selectedStoreId = state.selectedStoreId || breakdown[0]?.restaurant_id || '';
    const dataType = state.dataType;

    /** 多选为空表示全部，否则只展示选中的门店卡片 */
    const displayBreakdown = useMemo(
        () => (selectedStoreIds.length === 0 ? breakdown : breakdown.filter((b) => selectedStoreIds.includes(b.restaurant_id))),
        [breakdown, selectedStoreIds]
    );

    /** 对比门店：选项为全部门店，多选；未选任何门店时视为“全部” */
    const storeOptions: Option[] = useMemo(
        () => breakdown.map((b) => ({ value: b.restaurant_id, label: b.restaurant_name ?? b.restaurant_id })),
        [breakdown]
    );
    // const selectedStoreOptions: Option[] = useMemo(() => {
    //     const uniqueIds = [...new Set(selectedStoreIds)];
    //     return uniqueIds.map((id) => {
    //         const b = breakdown.find((x) => x.restaurant_id === id);
    //         return { value: id, label: b?.restaurant_name ?? id };
    //     });
    // }, [breakdown, selectedStoreIds]);

    /** 折线图对应多选门店：每条线一个门店，横轴取第一个有 trend 的门店日期 */
    const lineChartStores = useMemo(
        () => displayBreakdown.filter((s) => s.trend && s.trend.length > 0),
        [displayBreakdown]
    );
    /** 折线图横轴原始 x */
    const lineChartRawXLabels = useMemo(
        () => lineChartStores[0]?.trend?.map((p) => p.x) ?? trend?.series?.map((p) => p.x) ?? [],
        [lineChartStores, trend]
    );
    /** 横轴时间粒度（本日为 hour） */
    const lineChartGranularity = trend?.granularity ?? state.dashboard?.granularity;
    const lineChartSeries = useMemo(() => {
        if (lineChartStores.length > 0) {
            return lineChartStores.map((store) => ({
                name: store.restaurant_name ?? store.restaurant_id,
                data: (store.trend ?? []).map((p) => p.revenue)
            }));
        }
        if (trend?.series?.length) {
            return [{ name: i18n.t('group_revenue_analysis_group_total'), data: trend.series.map((p) => p.revenue) }];
        }
        return [];
    }, [lineChartStores, trend]);

    /** 折线图主标题括号内范围：单店名 / 多店为 N 家门店 */
    const lineChartTitleSuffix = useMemo(() => {
        const n = lineChartSeries.length;
        if (n === 0) return '';
        if (n === 1) return ` (${lineChartSeries[0].name})`;
        return ` (${n} ${i18n.t('group_revenue_analysis_stores_unit')})`;
    }, [lineChartSeries]);

    /** 折线色：按图例/系列顺序轮询 token（与 App、createDashboardBusinessLineApexOptions 默认一致） */
    const multiStoreLineColors = useMemo(
        () => getLineChartSeriesColors(lineChartSeries.length),
        [lineChartSeries.length, lineChartSeries.map((s) => s.name).join('|')],
    );

    /** 多门店趋势 Apex options：基底 + 本页轴/tooltip */
    const multiStoreLineApexOptions = useMemo(
        () =>
            mergeBusinessLineApexOptions(
                createDashboardBusinessLineApexOptions(theme, {
                    rawXLabels: lineChartRawXLabels,
                    granularity: lineChartGranularity,
                    seriesCount: lineChartSeries.length,
                    height: '100%',
                    showLegend: false,
                    strokeWidth: 2,
                    lineColors: multiStoreLineColors,
                }),
                {
                    xaxis: {
                        labels: {
                            style: { colors: chartLabelColor, fontSize: '12px' },
                        },
                    },
                    yaxis: {
                        labels: {
                            style: { colors: chartLabelColor, fontSize: '12px' },
                            formatter: (value: number) => value.toString(),
                        },
                    },
                    grid: { borderColor: chartGridColor },
                },
            ),
        [
            theme,
            lineChartRawXLabels,
            lineChartGranularity,
            lineChartSeries.length,
            multiStoreLineColors,
            chartLabelColor,
            chartGridColor,
            lineChartSeries.map((s) => s.name).join('|'),
            fillsViewportOnLargeScreen,
        ],
    );

    /** 筛选行始终展示，不被骨架屏遮盖；宽度不足时整组换行 */
    const filterRow = (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, rowGap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                <Typography sx={{ fontWeight: 500, color: 'var(--text-dark)', whiteSpace: 'nowrap', fontSize: 24, lineHeight: '32px' }}>
                    {i18n.t('group_revenue_analysis_data_type')}
                </Typography>
                <CommonInput
                    inputType="SELECT_PILL"
                    size="small"
                    select
                    items={DATA_TYPE_OPTIONS.map((o) => ({ value: o.value, label: i18n.t(o.label) }))}
                    value={dataType}
                    onChange={(e) => {
                        state.setDataType(e.target.value as string);

                        if (effectiveGroupId) void state.fetchDashboardTrendForTab1(effectiveGroupId);
                    }}
                    sx={{ minWidth: 164 }}
                />
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 500, color: 'var(--text-dark)', whiteSpace: 'nowrap', fontSize: 24, lineHeight: '32px' }}>
                    {i18n.t('group_revenue_analysis_compare_stores')}
                </Typography>
                <CommonInput
                    inputType="SELECT_PILL_MUL"
                    value={selectedStoreIds}
                    onChange={(e) => {
                        state.setSelectedStoreIds((e.target?.value as unknown as string[]) ?? []);
                    }}
                    items={storeOptions}
                    placeholder={selectedStoreIds.length === 0 ? i18n.t('ALL') : undefined}
                    sx={{ minWidth: 220 }}
                    size="small"
                />
            </Box>
        </Box>
    );

    if (loading) {
        return (
            <MultiStoreComparisonSkeleton
                filterRow={filterRow}
                fillsViewportOnLargeScreen={fillsViewportOnLargeScreen}
            />
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                p: 1,
                ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: '100%' } : {}),
            }}
        >
            {filterRow}
            {/* 折线图区域：min 480px；大屏 flex 伸展占满筛选与门店卡之间的剩余高度 */}
            <Box
                sx={{
                    bgcolor: 'var(--fill-neutral-100)',
                    borderRadius: '16px',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    boxShadow: '0px 0px 4px var(--shadow-25)',
                    minHeight: GROUP_REVENUE_MULTI_STORE_LINE_CHART_CARD_MIN_HEIGHT_PX,
                    overflow: 'visible',
                    ...(fillsViewportOnLargeScreen ? { flex: 1 } : { flexShrink: 0 }),
                }}
            >
                {/* 标题单行居中；次行左侧「金额」、右侧横向图例（色点+名称） */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexShrink: 0 }}>
                    <Typography variant="h5" sx={{ color: 'var(--text-primary-300)', fontWeight: 'bold', textAlign: 'center', width: '100%' }}>
                        {i18n.t('group_revenue_analysis_revenue_chart_title')}
                        {lineChartTitleSuffix}
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 2,
                            flexWrap: 'wrap',
                        }}
                    >
                        <Typography variant="body2" sx={{ color: chartLabelColor, flexShrink: 0 }}>
                            {i18n.t('group_revenue_analysis_amount')}
                        </Typography>
                        {lineChartSeries.length > 0 ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 2,
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {lineChartSeries.map((s, idx) => (
                                    <Box key={`${s.name}-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                                        <Box
                                            sx={{
                                                width: 10,
                                                height: 10,
                                                borderRadius: '50%',
                                                bgcolor: multiStoreLineColors[idx % multiStoreLineColors.length],
                                                flexShrink: 0,
                                            }}
                                        />
                                        <Typography variant="body2" sx={{ color: 'var(--text-dark)', whiteSpace: 'nowrap' }}>
                                            {s.name}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : null}
                    </Box>
                </Box>
                <Box
                    className="multi-store-line-chart-root"
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        position: 'relative',
                        overflow: 'visible',
                        '& .apexcharts-tooltip': {
                            zIndex: 2000,
                            border: 'none',
                            boxShadow: 'none',
                            background: 'transparent',
                        },
                    }}
                >
                    <LinechartOfBusinessData
                        series={lineChartSeries}
                        apexOptions={multiStoreLineApexOptions}
                        height="100%"
                        rootId="line-chart-of-business-data-compare"
                        rawXLabels={lineChartRawXLabels}
                        xAxisGranularity={lineChartGranularity}
                    />
                </Box>
            </Box>
            {/* 多门店卡片：横向排列，宽度不足时自动换行；单卡 min 220px 保证饼图与图例完整显示 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, paddingLeft: 2, overflowY: 'visible', pb: 1, pt: 1, flexShrink: 0 }}>
                {displayBreakdown.map((store) => {
                    const hasData = (store.payment_breakdown?.length ?? 0) > 0;
                    return (
                        <Box
                            key={store.restaurant_id}
                            // onClick={() => state.setSelectedStoreId(store.restaurant_id)}
                            sx={{
                                flex: '0 0 220px',
                                minWidth: 220,
                                maxWidth: '100%',
                                minHeight: 280,
                                bgcolor: 'var(--fill-neutral-100)',
                                borderRadius: '16px',
                                p: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                boxShadow: '0px 0px 4px var(--shadow-25)',
                                // cursor: 'pointer',
                                // border: selectedStoreId === store.restaurant_id ? '2px solid var(--stroke-primary-300)' : '2px solid transparent',
                            }}
                        >
                            {/* 设计图：门店图标 + 门店名，居中，正文 20px；长店名最多两行自动换行 */}
                            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', minHeight: 56, p: 1 }}>
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1.25,
                                        maxWidth: '100%',
                                    }}
                                >
                                    <Box component="span" sx={{ color: 'var(--icon-primary-300)', display: 'inline-flex', flexShrink: 0 }}>
                                        <StoreRankingIcon style={{ width: 24, height: 24 }} />
                                    </Box>
                                    <Typography
                                        sx={{
                                            color: 'var(--text-dark)',
                                            fontSize: 20,
                                            lineHeight: 1.2,
                                            fontWeight: 500,
                                            minWidth: 0,
                                            flex: '1 1 auto',
                                            textAlign: 'center',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {store.restaurant_name ?? store.restaurant_id}
                                    </Typography>
                                </Box>
                            </Box>
                            {/* 设计图：营收总额 与 金额 同一行左右分布，正文 20px */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexShrink: 0 }}>
                                <Typography sx={{ color: 'var(--text-dark)', fontSize: 20, fontWeight: 500 }}>
                                    {i18n.t('group_revenue_analysis_revenue_total')}
                                </Typography>
                                <Typography sx={{ color: 'var(--text-dark)', fontSize: 20, fontWeight: 600 }}>
                                    {formatEUR(store.revenue)}
                                </Typography>
                            </Box>
                            {hasData ? (
                                <MultiStorePaymentPieSection store={store} />
                            ) : (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
                                    <Typography variant="body2" sx={{ color: chartLabelColor }}>
                                        {i18n.t('general_text_no_data')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    );
                })}
                {displayBreakdown.length === 0 && !loading && (
                    <Typography variant="body2" sx={{ color: chartLabelColor, py: 2 }}>
                        {i18n.t('general_text_no_data')}
                    </Typography>
                )}
            </Box>

        </Box>
    );
});

export default MultiStoreComparison;

/**
 * 门店卡片内支付方式饼图 + 滚动图例（扇区色与圆点一致，按 value 名次取色）。
 * @param store - 含 payment_breakdown 的 breakdown 项
 */
function MultiStorePaymentPieSection({
    store,
}: {
    store: {
        payment_breakdown?: { name: string; revenue: string | number }[];
    };
}) {
    const pb = store.payment_breakdown ?? [];
    const labels = pb.map((p) => p.name);
    const seriesData = pb.map((p) => parseFloat(String(p.revenue)) || 0);
    const pieColors = useMemo(
        () => getPieChartColorsByValues(seriesData, labels),
        [pb],
    );
    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                <PieChartOfBusinessData
                    data={seriesData}
                    labels={labels}
                    titleText=""
                    layout="dialog"
                    height={150}
                    dialogDataLabelFontSize="16px"
                    colors={pieColors}
                    containerSx={{
                        width: 150,
                        height: 150,
                        minWidth: 150,
                        maxWidth: 150,
                    }}
                />
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    flexShrink: 0,
                    maxHeight: 120,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                }}
            >
                {pb.map((item, idx) => (
                    <Box
                        key={item.name}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            width: '100%',
                            flexShrink: 0,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                            <Box
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    bgcolor: pieColors[idx % pieColors.length],
                                    flexShrink: 0,
                                }}
                            />
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-dark)',
                                    fontSize: 16,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {item.name}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'var(--text-dark)', fontSize: 16, flexShrink: 0 }}>
                            {formatEUR(parseFloat(String(item.revenue)) || 0)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </>
    );
}

function MultiStoreComparisonSkeleton({
    filterRow,
    fillsViewportOnLargeScreen,
}: {
    filterRow: React.ReactNode;
    fillsViewportOnLargeScreen: boolean;
}): React.ReactElement {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                p: 1,
                ...(fillsViewportOnLargeScreen ? { flex: 1, minHeight: '100%' } : {}),
            }}
        >
            {filterRow}
            <Box
                sx={{
                    bgcolor: 'var(--fill-neutral-100)',
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    boxShadow: '0px 0px 4px var(--shadow-25)',
                    minHeight: GROUP_REVENUE_MULTI_STORE_LINE_CHART_CARD_MIN_HEIGHT_PX,
                    ...(fillsViewportOnLargeScreen ? { flex: 1 } : { flexShrink: 0 }),
                }}
            >
                <Skeleton variant="text" width="50%" height={32} sx={{ alignSelf: 'center' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
                    <Skeleton variant="text" width="18%" height={20} />
                    <Skeleton variant="text" width="36%" height={20} />
                </Box>
                <Skeleton variant="rounded" sx={{ flex: 1, minHeight: 0, borderRadius: 1 }} />
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flexShrink: 0, pl: 0.5 }}>
                {[0, 1, 2].map((i) => (
                    <Skeleton
                        key={i}
                        variant="rounded"
                        sx={{
                            flex: '0 0 220px',
                            minWidth: 220,
                            maxWidth: '100%',
                            height: 360,
                            borderRadius: 3,
                        }}
                    />
                ))}
            </Box>
        </Box>
    );
}

